import { Component, Input, OnInit } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import {
  FRAME_MANUFACTURERS,
  SystemManufacturer,
  ProfileSystem,
  FrameProfile
} from '../../utils/frame-brands.data';
import { StorageService } from 'src/app/services/storage.service';
import { FrameProfilesModalComponent } from '../frame-profiles-modal/frame-profiles-modal.component';
import {
  getExpertImagePath,
  getPositionLabelConfig,
  roundTwoValuePositions,
  WINDOW_TYPE_EXPERT_IMAGES
} from '../../utils/Constants';
import { MasterdataApiService } from 'src/app/services/masterdata-api.service';
import { CalculationAutoUpdateService } from 'src/app/services/calculation-auto-update.service';
import { SystemSelectPopoverComponent } from '../system-select-popover/system-select-popover.component';
import { TranslateService } from '@ngx-translate/core';
import { calculateAreaWeightedUf } from 'src/app/utils/calculation';

@Component({
  selector: 'app-edit-frame',
  templateUrl: './edit-frame.component.html',
  styleUrls: ['./edit-frame.component.scss'],
  standalone: false,
})
export class EditFrameComponent implements OnInit {

  @Input() isComparisonMode: boolean = false;
  mode: string = 'by-frame';
  frameWidth: number = 0.11;
  selectedFrameId: number | null = null;
  selectedManufacturerId: number | null = null;
  selectedSystemId: number | null = null;
  manufacturers: SystemManufacturer[] = [];
  frameList: any[] = [];
  windowTypeId: number = 2;
  frameCount: number = 4;
  applyIdenticalInput: boolean = false;
  roundValuesEN: boolean = false;
  identicalUf: number = 1.1;
  identicalWidth: number = 0.08;
  // By-brand related
  selectedManufacturer: SystemManufacturer | null = null;
  filteredSystems: ProfileSystem[] = [];
  selectedSystem: ProfileSystem | null = null;
  frameTypeFilter: string | null = null;   // null = All
  frameUfFilter: string | null = null;     // null = All

  frameTypes: any[] = [
    { value: 'FRAME_TYPE_WOOD', label: 'FRAME_TYPE_WOOD' },
    { value: 'FRAME_TYPE_PVC', label: 'FRAME_TYPE_PVC' },
    { value: 'FRAME_TYPE_ALU_WOOD', label: 'FRAME_TYPE_ALU_WOOD' },
    { value: 'FRAME_TYPE_ALU_METAL', label: 'FRAME_TYPE_ALU_METAL' }
  ];

  frameData: Array<{ uf: number; w: number }> = [];
  manualFrame = {
    name: '',
    type: 'FRAME_TYPE_PVC'
  };

  constructor(private modalController: ModalController, private storageService: StorageService,
    private masterdataApi: MasterdataApiService, private calcAutoUpdate: CalculationAutoUpdateService,
    private popoverController: PopoverController, private translate: TranslateService) { this.initializeFrameData(); }

  async ngOnInit() {
    await this.loadManufacturers();
    this.initFrames();
    await this.loadWindowTypeFromStorage();
    this.updateFrameCount();
    this.initializeFrameData()
    await this.loadExistingFrame();

  }

  roundTwoValuePositions(value: number): string {
    return value.toFixed(2);
  }

  initFrames() {
    const frames: any[] = [];
    const width = 0.11;
    let uf: number;
    let uf_fixed: string;
    let i: number;

    // FRAME_TYPE_WOOD: id 1-11 (0.8 to 1.8)
    uf = 0.8;
    for (i = 1; i < 12; i++) {
      uf_fixed = this.roundTwoValuePositions(uf);
      uf = parseFloat(uf_fixed);

      frames.push({
        id: i,
        type: 'FRAME_TYPE_WOOD',
        uf: uf_fixed,
        width: width,
        name: '',
        img: 'assets/images/frame/Wood.svg',
        uf0: uf,
        uf1: uf,
        uf2: uf,
        uf3: uf,
        uf4: uf,
        uf5: uf,
        uf6: uf,
        w0: width,
        w1: width,
        w2: width,
        w3: width,
        w4: width,
        w5: width,
        w6: width
      });
      uf += 0.1;
    }

    // FRAME_TYPE_PVC: id 12-22 (0.8 to 1.9)
    uf = 0.8;
    for (i = 12; i < 23; i++) {
      uf_fixed = this.roundTwoValuePositions(uf);
      uf = parseFloat(uf_fixed);

      frames.push({
        id: i,
        type: 'FRAME_TYPE_PVC',
        uf: uf_fixed,
        width: width,
        name: '',
        img: 'assets/images/frame/pvc.svg',
        uf0: uf,
        uf1: uf,
        uf2: uf,
        uf3: uf,
        uf4: uf,
        uf5: uf,
        uf6: uf,
        w0: width,
        w1: width,
        w2: width,
        w3: width,
        w4: width,
        w5: width,
        w6: width
      });
      uf += 0.1;
    }

    // FRAME_TYPE_ALU_WOOD: id 23-31 (0.8 to 1.6)
    uf = 0.8;
    for (i = 23; i < 32; i++) {
      uf_fixed = this.roundTwoValuePositions(uf);
      uf = parseFloat(uf_fixed);

      frames.push({
        id: i,
        type: 'FRAME_TYPE_ALU_WOOD',
        uf: uf_fixed,
        width: width,
        name: '',
        img: 'assets/images/frame/Aluwood.svg',
        uf0: uf,
        uf1: uf,
        uf2: uf,
        uf3: uf,
        uf4: uf,
        uf5: uf,
        uf6: uf,
        w0: width,
        w1: width,
        w2: width,
        w3: width,
        w4: width,
        w5: width,
        w6: width
      });
      uf += 0.1;
    }

    // FRAME_TYPE_ALU_METAL: id 32-46 (1.0 to 2.4)
    uf = 1.0;
    for (i = 32; i < 47; i++) {
      uf_fixed = this.roundTwoValuePositions(uf);
      uf = parseFloat(uf_fixed);

      frames.push({
        id: i,
        type: 'FRAME_TYPE_ALU_METAL',
        uf: uf_fixed,
        width: width,
        name: '',
        img: 'assets/images/frame/Alumetal.svg',
        uf0: uf,
        uf1: uf,
        uf2: uf,
        uf3: uf,
        uf4: uf,
        uf5: uf,
        uf6: uf,
        w0: width,
        w1: width,
        w2: width,
        w3: width,
        w4: width,
        w5: width,
        w6: width
      });
      uf += 0.1;
    }

    this.frameList = frames;
  }

  async loadExistingFrame() {
    try {
      const res = this.isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();

      const storedProject = res ? JSON.parse(res) : null;

      if (storedProject?.windowframe) {
        const f = storedProject.windowframe;

        //Normalize: support both old (uf0..uf6) and new (ufValues) format
        const ufValues: number[] = f.ufValues ?? Array.from({ length: 7 }, (_, i) => f[`uf${i}`] ?? 0);
        const wValues: number[] = f.wValues ?? Array.from({ length: 7 }, (_, i) => f[`w${i}`] ?? 0);
        const visibleWidth = f.visibleWidth ?? f.width ?? 0.11;
        console.log('Loading saved frame:', f);

        // Determine mode based on saved frame
        if (f.system) {
          // By-brand mode
          this.mode = 'by-brand';
          this.selectedManufacturerId = f.system.manufacturerId ?? f.system.mid ?? null;
          this.selectedSystemId = f.system.systemId ?? f.system.sid ?? null;
          this.onManufacturerChange();

        } else if (f.id > 0) {
          // By-frame mode
          this.mode = 'by-frame';
          this.selectedFrameId = f.id;
          this.frameWidth = visibleWidth;

        } else {
          // Manual mode
          this.mode = 'manual';
          this.manualFrame.name = f.name || '';
          this.manualFrame.type = f.type || 'FRAME_TYPE_PVC';

          if (f.manualMode === 'identical') {
            this.applyIdenticalInput = true;
            this.identicalUf = ufValues[0] || 1.1;
            this.identicalWidth = wValues[0] || 0.08;
            this.applyIdenticalToAll();
          } else {
            this.applyIdenticalInput = false;
            this.frameData = [];
            for (let i = 0; i < this.frameCount; i++) {
              this.frameData.push({
                uf: ufValues[i] || 1.1,
                w: wValues[i] || 0.08
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading existing frame:', error);
    }
  }
  private checkIfIdentical(frame: any): boolean {
    const uf0 = parseFloat(frame.uf0);
    for (let i = 1; i < 7; i++) {
      if (parseFloat(frame[`uf${i}`]) !== uf0) {
        return false;
      }
    }
    return true;
  }

  //brand
  async loadWindowTypeFromStorage() {
    try {
      const res = this.isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();

      const storedProject = res ? JSON.parse(res) : null;

      console.log('Stored Project:', storedProject);
      console.log('Window Type:', storedProject?.windowtype);

      const id = storedProject?.windowtype ?? storedProject?.windowtype?.id;
      if (id) {
        this.windowTypeId = id;
        console.log('Loaded window type ID:', this.windowTypeId);
      }
    } catch (error) {
      console.error('Error loading window type:', error);
    }
  }

  async loadManufacturers() {
    // this.manufacturers = FRAME_MANUFACTURERS;
    // console.log('Loaded manufacturers:', this.manufacturers);
    const cached = await this.masterdataApi.getCachedMasterData();
    if (cached?.frameManufacturers?.length) {
      this.manufacturers = cached.frameManufacturers as SystemManufacturer[];
      console.log('Loaded manufacturers from masterdata:', this.manufacturers);
      return;
    }
    this.manufacturers = FRAME_MANUFACTURERS;
  }

  onManufacturerChange() {
    this.selectedManufacturer = this.manufacturers.find(m => m.id === this.selectedManufacturerId) || null;
    this.filteredSystems = this.selectedManufacturer?.systems || [];

    // Reset system selection
    this.selectedSystemId = null;
    this.selectedSystem = null;
  }

  onSystemChange() {
    this.selectedSystem = this.filteredSystems.find(s => s.id === this.selectedSystemId) || null;
  }

  onModeChange(event: any) {
    this.mode = event.detail.value;
  }

  selectFrame(frame: any) {
    this.selectedFrameId = frame.id;
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }

  isNextButtonDisabled(): boolean {
    if (this.mode === 'by-frame') {
      return !this.selectedFrameId;
    } else if (this.mode === 'by-brand') {
      return !this.selectedManufacturerId || !this.selectedSystemId;;
    } else if (this.mode === 'manual') {
      return !this.manualFrame.name || !this.manualFrame.type;
    }
    return true;
  }

  async openFrameProfilesModal() {
    const modal = await this.modalController.create({
      component: FrameProfilesModalComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        selectedManufacturerId: this.selectedManufacturerId,
        selectedSystemId: this.selectedSystemId,
        windowTypeId: this.windowTypeId,
        isComparisonMode: this.isComparisonMode
      }
    });

    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      await modal.dismiss('confirm');
      await this.modalController.dismiss(data, 'confirm');
    }
  }

  //manual
  //   Get frame count based on window type
  updateFrameCount() {
    const frameCounts: { [key: number]: number } = {
      1: 3, 2: 4, 3: 7, 4: 6, 5: 5, 6: 7,
      7: 5, 8: 6, 9: 3, 10: 4, 11: 5, 12: 7,
      13: 4, 14: 4, 15: 4, 16: 6
    };
    this.frameCount = frameCounts[this.windowTypeId] || 4;
  }

  initializeFrameData() {
    this.frameData = [];
    for (let i = 0; i < this.frameCount; i++) {
      this.frameData.push({
        uf: this.identicalUf,   // Default Uf value
        w: this.identicalWidth    // Default width value
      });
    }
  }
  //Get frame range for *ngFor loop
  getFrameRange(): number[] {
    return Array.from({ length: this.frameCount }, (_, i) => i);
  }

  //Get frame part image using constants helper
  getFramePartImage(index: number): string {
    const positionKey = `f${index}`;
    const imagePath = getExpertImagePath(this.windowTypeId, positionKey);

    console.log(`Getting image for ${positionKey}:`, imagePath);
    return imagePath;
  }

  //Get frame part label using constants helper
  getFramePartLabel(index: number): string {
    const positionKey = `f${index}`;
    const config = getPositionLabelConfig(this.windowTypeId, positionKey);
    return config ? this.translate.instant(config.main) : positionKey;
  }

  //Get frame part subtitle
  getFramePartSubtitle(index: number): string {
    const positionKey = `f${index}`;
    const config = getPositionLabelConfig(this.windowTypeId, positionKey);
    return config?.sub ? this.translate.instant(config.sub) : '';
  }

  onApplyIdenticalChange() {
    if (this.applyIdenticalInput) {
      // When checked, use first frame values as identical values
      if (this.frameData.length > 0) {
        this.identicalUf = this.frameData[0].uf;
        this.identicalWidth = this.frameData[0].w;
      }
      // Apply to all frames
      this.applyIdenticalToAll();
    }
  }

  onIdenticalUfChange() {
    if (this.applyIdenticalInput) {
      this.applyIdenticalToAll();
    }
  }

  onIdenticalWidthChange() {
    if (this.applyIdenticalInput) {
      this.applyIdenticalToAll();
    }
  }

  private applyIdenticalToAll() {
    const uf = this.roundValuesEN ? roundTwoValuePositions(this.identicalUf) : this.identicalUf;

    for (let i = 0; i < this.frameData.length; i++) {
      this.frameData[i].uf = uf;
      this.frameData[i].w = this.identicalWidth;
    }
  }

  onUfChange() {
    if (this.roundValuesEN) {
      // Apply rounding to all Uf values
      for (let i = 0; i < this.frameData.length; i++) {
        this.frameData[i].uf = roundTwoValuePositions(this.frameData[i].uf);
      }
    }
  }

  onRoundValuesChange() {
    if (this.roundValuesEN) {
      if (this.applyIdenticalInput) {
        // Round identical value and apply to all
        this.identicalUf = roundTwoValuePositions(this.identicalUf);
        this.applyIdenticalToAll();
      } else {
        // Round all individual values
        for (let i = 0; i < this.frameData.length; i++) {
          this.frameData[i].uf = roundTwoValuePositions(this.frameData[i].uf);
        }
      }
    }
  }

  calculateAverageUf(): number {
    if (this.frameData.length === 0) return 1.0;

    const sum = this.frameData.reduce((acc, frame) => acc + frame.uf, 0);
    return parseFloat((sum / this.frameData.length).toFixed(2));
  }

  getFrameImageByType(type: string): string {
    const imageMap: { [key: string]: string } = {
      'FRAME_TYPE_WOOD': 'assets/images/frame/Wood.svg',
      'FRAME_TYPE_PVC': 'assets/images/frame/pvc.svg',
      'FRAME_TYPE_ALU_WOOD': 'assets/images/frame/Aluwood.svg',
      'FRAME_TYPE_ALU_METAL': 'assets/images/frame/Alumetal.svg'
    };

    return imageMap[type] || 'assets/images/frame/pvc.svg';
  }

  //data saving

  async next() {
    let frameDataToSave: any;

    if (this.mode === 'by-frame') {
      if (!this.selectedFrameId) return;

      const selectedFrame = this.frameList.find((f: any) => f.id === this.selectedFrameId);
      selectedFrame.width = this.frameWidth;
      for (let i = 0; i < 7; i++) {
        selectedFrame[`w${i}`] = this.frameWidth;
      }
      frameDataToSave = this.prepareFrameData(selectedFrame, 'by-frame');

    } else if (this.mode === 'by-brand') {
      if (!this.selectedSystemId) {
        await this.openFrameProfilesModal();
        return;
      }

      await this.openFrameProfilesModal();
      return; // Will be handled by modal dismiss

    } else if (this.mode === 'manual') {
      if (this.applyIdenticalInput) {
        frameDataToSave = this.prepareFrameData(this.buildManualFrameIdentical(), 'by-frame');
      } else {
        frameDataToSave = this.prepareFrameData(this.buildManualFrame(), 'manual');
        frameDataToSave = await this.applyWeightedUfToFrame(frameDataToSave);
      }
    }

    // Save to storage
    if (frameDataToSave) {
      await this.saveFrameToStorage(frameDataToSave);
      this.modalController.dismiss({ frame: frameDataToSave }, 'confirm');
    }
  }

  private buildManualFrameIdentical(): any {
    return {
      id: 0, // ID 0 for identical manual frames
      manualMode: 'identical',
      type: this.manualFrame.type,
      uf: this.identicalUf,
      width: this.identicalWidth,
      name: this.manualFrame.name,
      img: this.getFrameImageByType(this.manualFrame.type)
    };
  }

  private buildManualFrame(): any {
    return {
      id: -1, // ✅ ID -1 for non-identical manual frames
      manualMode: 'non-identical',
      type: this.manualFrame.type,
      uf: 0,
      width: this.calculateAverageWidth(),
      name: this.manualFrame.name,
      img: this.getFrameImageByType(this.manualFrame.type)
    };
  }
  private prepareFrameData(frame: any, mode: string): any {
    const ufValues: number[] = new Array(7).fill(0);
    const wValues: number[] = new Array(7).fill(0);

    if (mode === 'by-frame') {
      const uf = parseFloat(frame.uf);
      const w = parseFloat(frame.width ?? frame.visibleWidth);
      ufValues.fill(uf);
      wValues.fill(w);
    } else {
      // manual non-identical: only fill used positions
      for (let i = 0; i < this.frameCount; i++) {
        ufValues[i] = Number(this.frameData[i].uf ?? 0);
        wValues[i] = Number(this.frameData[i].w ?? 0);
      }
    }

    return {
      id: frame.id ?? -1,
      manualMode: frame.manualMode,
      type: frame.type,
      uf: parseFloat(frame.uf ?? 0),
      visibleWidth: parseFloat(frame.width ?? frame.visibleWidth) || this.calculateAverageWidth(),
      name: frame.name || this.manualFrame.name || '',
      ufValues,
      wValues
    };
  }


  private calculateAverageWidth(): number {
    if (this.frameData.length === 0) return 0.11;
    const sum = this.frameData.reduce((acc, frame) => acc + frame.w, 0);
    return parseFloat((sum / this.frameData.length).toFixed(3));
  }

  private async saveFrameToStorage(frameData: any): Promise<void> {
    try {
      if (this.isComparisonMode) {
        this.storageService.updateComparedProject({ windowframe: frameData });
      } else {
        this.storageService.updateProject({
          windowframe: frameData
        });
        await this.calcAutoUpdate.triggerAutoUpdate('frame', this.isComparisonMode);
      }
      console.log('Frame saved to storage:', frameData);
    } catch (error) {
      console.error('Error saving frame to storage:', error);
    }
  }

  //filter
  get availableFrameTypes(): { value: string; label: string }[] {
    return this.frameTypes;
  }

  get availableUfOptions(): string[] {
    let frames = this.frameList;
    if (this.frameTypeFilter) {
      frames = frames.filter(frame => frame.type === this.frameTypeFilter);
    }
    const ufSet = new Set(
      frames.map(frame => String(frame.uf))
    );
    return Array.from(ufSet).sort((a, b) => parseFloat(a) - parseFloat(b));
  }

  get filteredFrameList(): any[] {
    return this.frameList.filter(frame => {
      if (this.frameTypeFilter && frame.type !== this.frameTypeFilter) {
        return false;
      }
      if (this.frameUfFilter && String(frame.uf) !== this.frameUfFilter) {
        return false;
      }
      return true;
    });
  }

  onFrameTypeFilterChange() {
    // if current uf is no longer valid for selected type, reset to All
    if (
      this.frameUfFilter &&
      !this.availableUfOptions.includes(this.frameUfFilter)
    ) {
      this.frameUfFilter = null;
    }
  }
  onFrameUfFilterChange() {
  }

  async openSystemPopover(ev: Event) {
    const popover = await this.popoverController.create({
      component: SystemSelectPopoverComponent,
      event: ev,
      translucent: false,
      side: 'bottom',
      alignment: 'start',
      cssClass: 'system-dropdown-popover',
      componentProps: {
        systems: this.filteredSystems,
        selectedSystemId: this.selectedSystemId
      }
    });

    await popover.present();

    const { data, role } = await popover.onWillDismiss();

    if (role === 'confirm' && data?.system) {
      this.selectedSystemId = data.system.id;
      this.selectedSystem = data.system;
      this.onSystemChange();
    }
  }

  private async applyWeightedUfToFrame(frameData: any): Promise<any> {
    try {
      const projectData = this.isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();

      const project = projectData ? JSON.parse(projectData) : null;

      const dimensions = {
        a: parseFloat(project?.dimensions?.a ?? 0),
        b: parseFloat(project?.dimensions?.b ?? 0),
        c: parseFloat(project?.dimensions?.c ?? 0)
      };

      const frameWidths = {
        w0: frameData.wValues[0] || 0,
        w1: frameData.wValues[1] || 0,
        w2: frameData.wValues[2] || 0,
        w3: frameData.wValues[3] || 0,
        w4: frameData.wValues[4] || 0,
        w5: frameData.wValues[5] || 0,
        w6: frameData.wValues[6] || 0
      };

      frameData.uf = calculateAreaWeightedUf(
        this.windowTypeId,
        dimensions,
        frameWidths,
        frameData.ufValues
      );

      return frameData;
    } catch (error) {
      console.error('Error calculating weighted Uf:', error);
      return frameData;
    }
  }
}
